import {
  AxpoCounterInvoice,
  AxpoCounterInvoiceBatchFileError,
  AxpoCounterInvoiceBatchFileResult,
  AxpoCounterInvoiceBatchOperationResult,
  AxpoSiteNameMapping,
  extractAxiosErrorMessage,
  isAxpoCounterInvoiceDownloadLroResponse,
  useAsyncOperation,
  useAxpoCounterInvoices,
  useAxpoSiteNameMappings,
  useCreateAxpoCounterInvoiceBatch,
  useDeleteAxpoCounterInvoice,
  useDeleteAxpoSiteNameMapping,
  useAxpoCounterInvoiceDownload,
  useSiteList,
  useUpsertAxpoSiteNameMapping,
} from "@inrange/building-manager-api-client";
import { Icons } from "@inrange/theme-components/icons";
import {
  ChangeEvent,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  Alert,
  Badge,
  Button,
  Col,
  Dropdown,
  Form,
  Row,
  Spinner,
  Table,
} from "react-bootstrap";
import { Link } from "react-router-dom";
import Select from "react-select";
import { useQueryClient } from "@tanstack/react-query";
import CollapsibleCard from "../site/CollapsibleCard";
import { OPERATIONAL_STATUSES } from "../site/operationalStatusUtils";

const formatDdMmYy = (iso: string): string => {
  const d = new Date(iso);
  const dd = String(d.getDate()).padStart(2, "0");
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const yy = String(d.getFullYear() % 100).padStart(2, "0");
  return `${dd}-${mm}-${yy}`;
};

const formatPeriod = (periodFrom: string, periodTo: string): string => {
  return `${formatDdMmYy(periodFrom)} to ${formatDdMmYy(periodTo)}`;
};

const formatAmount = (amount: string): string => {
  const num = Number(amount);
  if (Number.isNaN(num)) return amount;
  const sign = num < 0 ? "-" : "";
  return `${sign}£${Math.abs(num).toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
};

const formatGenerated = (iso: string): string => {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleString();
};

const sortKey = (name: string) => name.trim().replaceAll(" ", "").toLowerCase();

const batchSortKey = (r: {
  siteId: string;
  mpan?: string;
  month?: string;
  format: "pdf" | "xlsx";
}): [string, string, string, number] => [
  r.siteId ?? "",
  r.mpan ?? "",
  r.month ?? "",
  r.format === "xlsx" ? 0 : 1,
];

const compareBatchEntries = (
  a: { siteId: string; mpan?: string; month?: string; format: "pdf" | "xlsx" },
  b: { siteId: string; mpan?: string; month?: string; format: "pdf" | "xlsx" }
): number => {
  const ka = batchSortKey(a);
  const kb = batchSortKey(b);
  for (let i = 0; i < ka.length; i++) {
    if (ka[i] < kb[i]) return -1;
    if (ka[i] > kb[i]) return 1;
  }
  return 0;
};

const formatBatchEntryHeader = (entry: {
  siteName?: string;
  mpan?: string;
  exportGridConnectionId?: string;
  month?: string;
  format: "pdf" | "xlsx";
  fileName: string;
}): string => {
  const mpan = entry.mpan ?? entry.exportGridConnectionId;
  const parts: string[] = [];
  if (entry.siteName) parts.push(entry.siteName);
  if (mpan) parts.push(mpan);
  if (entry.month) parts.push(entry.month);
  parts.push(`.${entry.format}`);
  return parts.join(", ");
};

const newestSourceKind = (
  row: AxpoCounterInvoice
): "pdf" | "xlsx" | null => {
  if (!row.pdfUploadedAt && !row.xlsxUploadedAt) return null;
  if (!row.pdfUploadedAt) return "xlsx";
  if (!row.xlsxUploadedAt) return "pdf";
  return new Date(row.xlsxUploadedAt) >= new Date(row.pdfUploadedAt)
    ? "xlsx"
    : "pdf";
};

interface SiteOption {
  label: string;
  value: string;
  name: string;
}

const AxpoInvoicesCard = () => {
  const queryClient = useQueryClient();
  const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [mappingError, setMappingError] = useState<string | null>(null);
  const [selectedSite, setSelectedSite] = useState<SiteOption | null>(null);
  const [axpoNameInput, setAxpoNameInput] = useState("");
  // Async-poll state for the batch LRO. The LRO runs across N files
  // (any mix of PDF + XLSX, any number of sites) and reports per-
  // file progress + a result manifest on completion.
  const [pendingOperation, setPendingOperation] = useState<{
    siteId: string;
    operationKey: string;
  } | null>(null);
  const [batchResult, setBatchResult] =
    useState<AxpoCounterInvoiceBatchOperationResult | null>(null);
  const [uploadProgress, setUploadProgress] = useState<{
    current: number;
    total: number;
  } | null>(null);
  // Per-row spinner state for the on-demand counter-PDF conversion.
  // The conversion runs as an LRO so the LibreOffice cold start doesn't
  // bump into the API GW 29s timeout; we poll /async_status and re-call
  // the download endpoint when the LRO finishes to get a fresh URL.
  const [pendingPdfDownload, setPendingPdfDownload] = useState<{
    pairingId: string;
    siteId: string;
    operationKey: string;
  } | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const { fetchDownloadUrl } = useAxpoCounterInvoiceDownload();
  const createBatch = useCreateAxpoCounterInvoiceBatch();
  const listQuery = useAxpoCounterInvoices();
  const deleteCounterInvoice = useDeleteAxpoCounterInvoice();

  const mappingsQuery = useAxpoSiteNameMappings();
  const upsertMapping = useUpsertAxpoSiteNameMapping();
  const deleteMapping = useDeleteAxpoSiteNameMapping();
  const { fetchSites } = useSiteList();

  const asyncOperation = useAsyncOperation({
    siteId: pendingOperation?.siteId ?? "",
    operationKey: pendingOperation?.operationKey ?? null,
  });

  const pdfDownloadOperation = useAsyncOperation({
    siteId: pendingPdfDownload?.siteId ?? "",
    operationKey: pendingPdfDownload?.operationKey ?? null,
  });

  // When the batch LRO completes, refresh the counter-invoice list,
  // clear the pending state, and surface per-file actions in the
  // success alert.
  useEffect(() => {
    if (!pendingOperation || !asyncOperation.data) return;
    const status = asyncOperation.data.status;
    if (status === "completed") {
      queryClient.invalidateQueries(["axpo-counter-invoices"]);
      const result = asyncOperation.data.result as
        | AxpoCounterInvoiceBatchOperationResult
        | undefined;
      if (result) setBatchResult(result);
      setPendingOperation(null);
    } else if (status === "failed") {
      setErrorMessage(
        asyncOperation.data.error ?? "Counter-invoice generation failed."
      );
      setPendingOperation(null);
    }
  }, [asyncOperation.data, pendingOperation, queryClient]);

  useEffect(() => {
    if (asyncOperation.timedOut && pendingOperation) {
      setErrorMessage("Counter-invoice operation timed out after 15 minutes.");
      setPendingOperation(null);
    }
  }, [asyncOperation.timedOut, pendingOperation]);

  // Counter-PDF conversion LRO: on completion re-call the download
  // endpoint (cache hit this time) and open the fresh presigned URL.
  // On failure or timeout surface the error and clear the spinner.
  useEffect(() => {
    if (!pendingPdfDownload || !pdfDownloadOperation.data) return;
    const status = pdfDownloadOperation.data.status;
    if (status === "completed") {
      const pairingId = pendingPdfDownload.pairingId;
      setPendingPdfDownload(null);
      (async () => {
        try {
          const response = await fetchDownloadUrl(pairingId, "counter-pdf");
          if (!isAxpoCounterInvoiceDownloadLroResponse(response)) {
            window.open(response.url, "_blank", "noopener,noreferrer");
          } else {
            setErrorMessage(
              "Counter-PDF was reported complete but the download endpoint still returned a pending operation."
            );
          }
        } catch (error) {
          const message = await extractAxiosErrorMessage(error);
          setErrorMessage(message);
        }
      })();
    } else if (status === "failed") {
      setErrorMessage(
        pdfDownloadOperation.data.error ??
          "Counter-PDF conversion failed."
      );
      setPendingPdfDownload(null);
    }
  }, [pdfDownloadOperation.data, pendingPdfDownload, fetchDownloadUrl]);

  useEffect(() => {
    if (pdfDownloadOperation.timedOut && pendingPdfDownload) {
      setErrorMessage("Counter-PDF conversion timed out after 15 minutes.");
      setPendingPdfDownload(null);
    }
  }, [pdfDownloadOperation.timedOut, pendingPdfDownload]);

  const operationalSiteOptions: SiteOption[] = useMemo(() => {
    const sites = (fetchSites.data?.sites || []) as {
      id: string;
      name: string;
      operationalStatus: string;
    }[];
    return sites
      .filter((site) => OPERATIONAL_STATUSES.has(site.operationalStatus))
      .map((site) => ({
        label: site.name,
        value: site.id,
        name: site.name,
      }))
      .sort((a, b) => sortKey(a.name).localeCompare(sortKey(b.name)));
  }, [fetchSites.data?.sites]);

  const mappings: AxpoSiteNameMapping[] = useMemo(
    () => mappingsQuery.data?.axpoSiteNameMappings ?? [],
    [mappingsQuery.data]
  );

  const handleFileChange = (e: ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files ? Array.from(e.target.files) : [];
    setSelectedFiles(files);
    setErrorMessage(null);
    setBatchResult(null);
  };

  const handleSubmit = useCallback(async () => {
    if (selectedFiles.length === 0) return;
    setErrorMessage(null);
    setBatchResult(null);
    setUploadProgress(null);
    try {
      const response = await createBatch.mutateAsync({
        files: selectedFiles,
        onUploadProgress: (current, total) =>
          setUploadProgress({ current, total }),
      });
      setUploadProgress(null);
      setSelectedFiles([]);
      if (fileInputRef.current) fileInputRef.current.value = "";
      setPendingOperation({
        siteId: response.siteId,
        operationKey: response.operationKey,
      });
    } catch (error) {
      setUploadProgress(null);
      const message = await extractAxiosErrorMessage(error);
      setErrorMessage(message);
    }
  }, [createBatch, selectedFiles]);

  const handleDownload = useCallback(
    async (
      pairingId: string,
      kind: "source-pdf" | "source-xlsx" | "counter" | "counter-pdf"
    ) => {
      try {
        const response = await fetchDownloadUrl(pairingId, kind);
        if (isAxpoCounterInvoiceDownloadLroResponse(response)) {
          // counter-pdf path can return a 202 + LRO handle when the
          // conversion isn't cached. Start polling and let the
          // useEffect above reopen the download once it's done.
          setPendingPdfDownload({
            pairingId,
            siteId: response.siteId,
            operationKey: response.operationKey,
          });
          return;
        }
        window.open(response.url, "_blank", "noopener,noreferrer");
      } catch (error) {
        const message = await extractAxiosErrorMessage(error);
        setErrorMessage(message);
      }
    },
    [fetchDownloadUrl]
  );

  const handleDeleteCounterInvoice = useCallback(
    async (pairingId: string) => {
      setErrorMessage(null);
      if (
        !window.confirm(
          "Delete this counter-invoice? The document number will be freed for re-upload."
        )
      ) {
        return;
      }
      try {
        await deleteCounterInvoice.mutateAsync(pairingId);
      } catch (error) {
        const message = await extractAxiosErrorMessage(error);
        setErrorMessage(message);
      }
    },
    [deleteCounterInvoice]
  );

  const handleUpsertMapping = useCallback(async () => {
    setMappingError(null);
    if (!selectedSite) {
      setMappingError("Pick a site first");
      return;
    }
    const trimmed = axpoNameInput.trim();
    if (!trimmed) {
      setMappingError("Enter the Axpo display name");
      return;
    }
    try {
      await upsertMapping.mutateAsync({
        siteId: selectedSite.value,
        axpoSiteName: trimmed,
      });
      setAxpoNameInput("");
      setSelectedSite(null);
    } catch (error) {
      const message = await extractAxiosErrorMessage(error);
      setMappingError(message);
    }
  }, [axpoNameInput, selectedSite, upsertMapping]);

  const handleDeleteMapping = useCallback(
    async (siteId: string) => {
      setMappingError(null);
      try {
        await deleteMapping.mutateAsync(siteId);
      } catch (error) {
        const message = await extractAxiosErrorMessage(error);
        setMappingError(message);
      }
    },
    [deleteMapping]
  );

  const existingMappingForSelected = useMemo(() => {
    if (!selectedSite) return undefined;
    return mappings.find((m) => m.siteId === selectedSite.value);
  }, [mappings, selectedSite]);

  const rows: AxpoCounterInvoice[] = useMemo(
    () => listQuery.data?.axpoCounterInvoices ?? [],
    [listQuery.data]
  );

  const isProcessing = createBatch.isLoading || pendingOperation !== null;
  const lroStatusMessage = pendingOperation
    ? (asyncOperation.data?.progress?.message ??
        (asyncOperation.data?.status === "running" ? "Processing..." : "Queued..."))
    : uploadProgress
      ? `Uploading ${uploadProgress.current} of ${uploadProgress.total}...`
      : null;

  return (
    <CollapsibleCard title="Axpo Invoices" initialOpen={false}>
      <div className="d-flex flex-column gap-3">
        <Row className="g-2 align-items-end">
          <Col style={{ width: "360px", flex: "0 0 auto" }}>
            <Form.Group controlId="axpoCounterInvoiceFile">
              <Form.Label>Axpo invoice files (.pdf and/or .xlsx)</Form.Label>
              <Form.Control
                type="file"
                size="sm"
                accept=".pdf,.xlsx,.xls"
                multiple
                onChange={handleFileChange}
                disabled={isProcessing}
                ref={fileInputRef}
              />
            </Form.Group>
          </Col>
          <Col sm="auto">
            <Button
              variant="primary"
              size="sm"
              onClick={handleSubmit}
              disabled={selectedFiles.length === 0 || isProcessing}
            >
              {isProcessing ? (
                <>
                  <Spinner
                    as="span"
                    animation="border"
                    size="sm"
                    role="status"
                    aria-hidden="true"
                    className="me-2"
                  />
                  {lroStatusMessage ?? "Generating..."}
                </>
              ) : selectedFiles.length > 1 ? (
                `Load data + generate ${selectedFiles.length} invoices`
              ) : (
                "Load data + generate invoice"
              )}
            </Button>
          </Col>
        </Row>

        {errorMessage && (
          <Alert
            variant="danger"
            dismissible
            onClose={() => setErrorMessage(null)}
          >
            {errorMessage}
          </Alert>
        )}

        {batchResult && (
          <Alert
            variant={batchResult.filesErrored > 0 ? "warning" : "success"}
            dismissible
            onClose={() => setBatchResult(null)}
          >
            <div>
              {batchResult.filesProcessed} of{" "}
              {batchResult.filesProcessed + batchResult.filesErrored} file(s)
              processed successfully.
            </div>
            {[...batchResult.fileResults]
              .sort(compareBatchEntries)
              .map((fr: AxpoCounterInvoiceBatchFileResult, i: number) => (
                <div key={`ok-${i}`} className="mt-2">
                  <div>
                    <strong>{formatBatchEntryHeader(fr)}</strong>
                  </div>
                  <div className="text-muted small">{fr.fileName}</div>
                  {fr.format === "xlsx" && (
                    <div>
                      {fr.readingsProcessed
                        ? `${fr.readingsCount ?? 0} readings loaded`
                        : `${fr.readingsCount ?? 0} readings skipped (newer data exists)`}
                      {(fr.sellerFilesCreated ?? 0) > 0 ||
                      (fr.buyerFilesCreated ?? 0) > 0
                        ? ", "
                        : ""}
                      {(fr.sellerFilesCreated ?? 0) > 0 &&
                      (fr.buyerFilesCreated ?? 0) > 0
                        ? "files attached to exporting site and importing site"
                        : (fr.sellerFilesCreated ?? 0) > 0
                          ? "file attached to exporting site"
                          : (fr.buyerFilesCreated ?? 0) > 0
                            ? "file attached to importing site"
                            : ""}
                    </div>
                  )}
                  {fr.format === "pdf" && fr.lineItemsCount != null && (
                    <div>
                      {fr.lineItemsCount} line item
                      {fr.lineItemsCount === 1 ? "" : "s"} persisted from PDF
                    </div>
                  )}
                </div>
              ))}
            {/* Per-file error strings come from BMA's batch LRO and
                include parser-side failures (e.g. internal-consistency
                validation messages surfaced by the Axpo PDF parser). */}
            {[...batchResult.errors]
              .sort(compareBatchEntries)
              .map((er: AxpoCounterInvoiceBatchFileError, i: number) => (
                <div key={`err-${i}`} className="mt-2 text-danger">
                  <div>
                    <strong>{formatBatchEntryHeader(er)}</strong>
                  </div>
                  <div className="text-muted small">{er.fileName}</div>
                  <div>{er.error}</div>
                </div>
              ))}
          </Alert>
        )}

        {listQuery.isLoading ? (
          <div className="text-muted">Loading counter-invoices...</div>
        ) : rows.length === 0 ? (
          <div className="text-muted">No counter-invoices generated yet.</div>
        ) : (
          <div style={{ maxHeight: "330px", overflowY: "auto" }}>
          <Table
            striped
            bordered
            hover
            size="sm"
            className="align-middle"
            style={
              {
                fontSize: 14,
                marginBottom: 0,
                "--bs-table-cell-padding-y-sm": "0.15rem",
              } as React.CSSProperties
            }
          >
            <thead
              style={{
                position: "sticky",
                top: 0,
                backgroundColor: "var(--bs-body-bg)",
                zIndex: 1,
              }}
            >
              <tr>
                <th>Site</th>
                <th>MPAN</th>
                <th>Period</th>
                <th>Amount (GBP)</th>
                <th>Invoice #</th>
                <th>Generated at</th>
                <th></th>
                <th></th>
                <th></th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => {
                const newer = newestSourceKind(row);
                const hasPdf = Boolean(
                  row.pdfSourceFileName && row.pdfUploadedAt
                );
                const hasXlsx = Boolean(
                  row.xlsxSourceFileName && row.xlsxUploadedAt
                );
                return (
                  <tr key={row.id}>
                    <td>
                      <Link to={`/site/${row.matchedSiteId}/edit`}>
                        {row.matchedSiteName}
                      </Link>
                    </td>
                    <td>{row.mpan}</td>
                    <td>{formatPeriod(row.periodFrom, row.periodTo)}</td>
                    <td>{formatAmount(row.netAmountGbp)}</td>
                    <td>INV-{row.invoiceNumber}</td>
                    <td>{formatGenerated(row.generatedAt)}</td>
                    <td>
                      {hasPdf && hasXlsx ? (
                        <Dropdown>
                          <Dropdown.Toggle
                            size="sm"
                            variant="outline-secondary"
                          >
                            Axpo invoice
                          </Dropdown.Toggle>
                          <Dropdown.Menu>
                            <Dropdown.Item
                              onClick={() =>
                                handleDownload(row.id, "source-pdf")
                              }
                            >
                              <div className="d-flex justify-content-between align-items-center gap-3">
                                <span>Axpo invoice (.pdf)</span>
                                {newer === "pdf" ? (
                                  <Badge bg="info" pill>
                                    newer
                                  </Badge>
                                ) : null}
                              </div>
                              <div className="text-muted small">
                                uploaded {formatGenerated(row.pdfUploadedAt!)}
                              </div>
                            </Dropdown.Item>
                            <Dropdown.Item
                              onClick={() =>
                                handleDownload(row.id, "source-xlsx")
                              }
                            >
                              <div className="d-flex justify-content-between align-items-center gap-3">
                                <span>Axpo invoice (.xlsx)</span>
                                {newer === "xlsx" ? (
                                  <Badge bg="info" pill>
                                    newer
                                  </Badge>
                                ) : null}
                              </div>
                              <div className="text-muted small">
                                uploaded {formatGenerated(row.xlsxUploadedAt!)}
                              </div>
                            </Dropdown.Item>
                          </Dropdown.Menu>
                        </Dropdown>
                      ) : hasPdf ? (
                        <Button
                          size="sm"
                          variant="outline-secondary"
                          onClick={() =>
                            handleDownload(row.id, "source-pdf")
                          }
                        >
                          Axpo invoice (.pdf)
                        </Button>
                      ) : hasXlsx ? (
                        <Button
                          size="sm"
                          variant="outline-secondary"
                          onClick={() =>
                            handleDownload(row.id, "source-xlsx")
                          }
                        >
                          Axpo invoice (.xlsx)
                        </Button>
                      ) : null}
                    </td>
                    <td>
                      <Button
                        size="sm"
                        variant="outline-secondary"
                        onClick={() => handleDownload(row.id, "counter")}
                      >
                        Counter-invoice (.docx)
                      </Button>
                    </td>
                    <td>
                      {(() => {
                        const isThisRowConverting =
                          pendingPdfDownload?.pairingId === row.id;
                        const isAnyRowConverting =
                          pendingPdfDownload !== null;
                        return (
                          <Button
                            size="sm"
                            variant="primary"
                            onClick={() => handleDownload(row.id, "counter-pdf")}
                            disabled={isAnyRowConverting}
                          >
                            {isThisRowConverting ? (
                              <>
                                <Spinner
                                  as="span"
                                  animation="border"
                                  size="sm"
                                  role="status"
                                  aria-hidden="true"
                                  className="me-2"
                                />
                                Converting...
                              </>
                            ) : (
                              "Counter-invoice (.pdf)"
                            )}
                          </Button>
                        );
                      })()}
                    </td>
                    <td style={{ textAlign: "center", whiteSpace: "nowrap" }}>
                      <img
                        alt="delete"
                        src={Icons.trashcanRed}
                        style={{
                          width: "16px",
                          height: "16px",
                          cursor: deleteCounterInvoice.isLoading
                            ? "not-allowed"
                            : "pointer",
                          opacity: deleteCounterInvoice.isLoading ? 0.5 : 1,
                        }}
                        onClick={() => {
                          if (deleteCounterInvoice.isLoading) return;
                          handleDeleteCounterInvoice(row.id);
                        }}
                      />
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </Table>
          </div>
        )}

        <hr />

        <Row className="g-2 align-items-end">
          <Col style={{ width: "300px", flex: "0 0 auto" }}>
            <Form.Group controlId="axpoSiteMappingSite">
              <Form.Label>InRange site</Form.Label>
              <Select
                inputId="axpoSiteMappingSite"
                options={operationalSiteOptions}
                value={selectedSite}
                onChange={(option) => {
                  setSelectedSite(option);
                  setAxpoNameInput(
                    option
                      ? mappings.find((m) => m.siteId === option.value)
                          ?.axpoSiteName ?? ""
                      : ""
                  );
                }}
                isClearable
                placeholder="Select operational site..."
                styles={{
                  control: (base) => ({
                    ...base,
                    minHeight: 31,
                    fontSize: 14,
                  }),
                  valueContainer: (base) => ({
                    ...base,
                    padding: "0 6px",
                  }),
                  input: (base) => ({ ...base, margin: 0, padding: 0 }),
                  dropdownIndicator: (base) => ({ ...base, padding: 4 }),
                  clearIndicator: (base) => ({ ...base, padding: 4 }),
                }}
              />
            </Form.Group>
          </Col>
          <Col style={{ width: "300px", flex: "0 0 auto" }}>
            <Form.Group controlId="axpoSiteMappingName">
              <Form.Label>Axpo display name</Form.Label>
              <Form.Control
                type="text"
                size="sm"
                value={axpoNameInput}
                onChange={(e) => setAxpoNameInput(e.target.value)}
                placeholder="e.g. Severn Beach (IM)"
              />
            </Form.Group>
          </Col>
          <Col sm="auto">
            <Button
              variant="primary"
              size="sm"
              onClick={handleUpsertMapping}
              disabled={
                !selectedSite ||
                !axpoNameInput.trim() ||
                upsertMapping.isLoading
              }
            >
              {existingMappingForSelected ? "Update" : "Add"}
            </Button>
          </Col>
        </Row>

        {mappingError && (
          <Alert
            variant="danger"
            dismissible
            onClose={() => setMappingError(null)}
          >
            {mappingError}
          </Alert>
        )}

        {mappingsQuery.isLoading ? (
          <div className="text-muted">Loading mappings...</div>
        ) : mappings.length === 0 ? (
          <div className="text-muted">No site name mappings configured.</div>
        ) : (
          <div style={{ maxHeight: "330px", overflowY: "auto" }}>
          <Table
            striped
            bordered
            hover
            size="sm"
            className="align-middle"
            style={
              {
                fontSize: 14,
                marginBottom: 0,
                "--bs-table-cell-padding-y-sm": "0.15rem",
              } as React.CSSProperties
            }
          >
            <thead
              style={{
                position: "sticky",
                top: 0,
                backgroundColor: "var(--bs-body-bg)",
                zIndex: 1,
              }}
            >
              <tr>
                <th>InRange site</th>
                <th>Axpo display name</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {mappings.map((mapping) => (
                <tr key={mapping.siteId}>
                  <td>
                    <Link to={`/site/${mapping.siteId}/edit`}>
                      {mapping.siteName}
                    </Link>
                  </td>
                  <td>{mapping.axpoSiteName}</td>
                  <td style={{ textAlign: "center", whiteSpace: "nowrap" }}>
                    <img
                      alt="delete"
                      src={Icons.trashcanRed}
                      style={{
                        width: "16px",
                        height: "16px",
                        cursor: deleteMapping.isLoading
                          ? "not-allowed"
                          : "pointer",
                        opacity: deleteMapping.isLoading ? 0.5 : 1,
                      }}
                      onClick={() => {
                        if (deleteMapping.isLoading) return;
                        handleDeleteMapping(mapping.siteId);
                      }}
                    />
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
          </div>
        )}
      </div>
    </CollapsibleCard>
  );
};

export default AxpoInvoicesCard;
